import { z } from "zod";

import {
  ctaBannerSectionSchema,
  heroSectionSchema,
  listSectionSchema,
  newsletterSectionSchema,
  pageSeoSchema,
  profileSectionSchema,
  resourcesSectionSchema,
  videoSectionSchema,
  workshopsListSectionSchema,
} from "@/content/schemas";
import { siteConfig } from "@/content/site";
import { validateContent } from "@/content/validateContent";

const homePageSchema = z.object({
  seo: pageSeoSchema,
  hero: heroSectionSchema,
  brandIntro: z.object({
    title: z.string().min(1),
    paragraphs: z.array(z.string().min(1)).min(1),
    highlight: z.string().optional(),
  }),
  pillarsSection: listSectionSchema,
  newsletterSection: newsletterSectionSchema,
  workshopsSection: workshopsListSectionSchema,
  youtubeSection: videoSectionSchema,
  instructorSection: profileSectionSchema,
  finalCta: ctaBannerSectionSchema,
});

export const homePageContent = validateContent("homePage", homePageSchema, {
  seo: {
    title: "Tech Savvy Starts Here | Practical AI and Digital Skills for Real Life",
    description:
      "Practical AI and digital skills for real life. Subscribe to the newsletter, watch YouTube content, and explore beginner-friendly resources.",
    canonicalPath: "/",
    socialImage: "/social-card.svg",
  },
  hero: {
    eyebrow: "Tech Savvy Starts Here",
    title: "Practical AI and Digital Skills",
    accent: "for Real Life",
    description:
      "Tech Savvy Starts Here helps you use AI and digital tools with more clarity, confidence, and purpose.",
    secondaryDescription: "For beginners, professionals, creators, parents, and small business owners.",
    primaryCta: {
      label: "Subscribe to the Newsletter",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "Explore the Workshop",
      href: "/workshops",
      external: false,
    },
    media: {
      type: "image",
      imageSrc: "/home-hero.png",
      imageAlt: "Woman working on a laptop in a bright home office",
    },
  },
  brandIntro: {
    title: "Start Where You Are. Grow Your Digital Confidence.",
    paragraphs: [
      "Tech Savvy Starts Here is a practical learning brand focused on helping everyday people understand AI, build useful digital skills, and use technology more effectively at work, in business, and at home.",
    ],
    highlight: "Simple guidance. Real use cases. Modern digital confidence.",
  },
  pillarsSection: {
    title: "What You Will Learn Here",
    description: "Explore the four content pillars that shape the brand.",
    items: [
      {
        title: "AI for beginners",
        description: "Start with the fundamentals and understand how AI can help you in everyday life and work.",
        icon: "Sparkles",
      },
      {
        title: "Digital skills for everyday life",
        description: "Build confidence with the practical tools and habits that make technology easier to use.",
        icon: "Users",
      },
      {
        title: "Practical tools for work and business",
        description: "Use AI and digital systems to improve workflow, communication, and execution.",
        icon: "Briefcase",
      },
      {
        title: "Content and online presence with AI",
        description: "Create clearer messaging, stronger websites, and smarter workflows for visibility online.",
        icon: "Layout",
      },
    ],
  },
  newsletterSection: {
    title: "Get Practical Tech Insights in Your Inbox",
    description:
      "Subscribe to the Tech Savvy newsletter for practical lessons, beginner friendly insights, and real world ways to use AI and digital tools.",
    embed: {
      title: "Beehiiv subscribe form",
      embedHtml: siteConfig.beehiivEmbedHtml,
      placeholderLabel: "Beehiiv embed slot for the homepage newsletter section",
      aspectRatio: "form",
    },
    primaryCta: {
      label: "Subscribe Now",
      href: siteConfig.beehiivSubscribeUrl,
      external: true,
    },
    secondaryCta: {
      label: "Browse Past Issues",
      href: siteConfig.beehiivPublicationUrl,
      external: true,
    },
  },
  workshopsSection: {
    title: "Learn Live",
    description: "Live workshops return when a new cohort is ready. For now, join the list for updates.",
    cards: [
      {
        status: "No current live workshop",
        title: "The next live workshop is not open yet",
        description:
          "The website-building workshop is currently between cohorts. Subscribe to get the next date, topic, and registration link when a new session goes live.",
        details: [
          "Next date: To be announced",
          "Registration: Currently closed",
        ],
        cta: {
          label: "See Workshop Updates",
          href: "/workshops",
          external: false,
        },
      },
    ],
  },
  youtubeSection: {
    title: "Watch and Learn",
    description:
      "Explore videos that break down AI, digital skills, and practical tech topics in a simple and approachable way.",
    featured: {
      title: "Featured Video",
      embedUrl: siteConfig.youtubeFeaturedEmbedUrl,
      placeholderLabel: "Featured Video",
    },
    primaryCta: {
      label: "Watch on YouTube",
      href: "/youtube",
      external: false,
    },
  },
  instructorSection: {
    title: "Meet Dr. Ijeoma Ndu",
    name: "Dr. Ijeoma Ndu",
    role: "ML Engineer and AI Educator",
    description:
      "Dr. Ijeoma Ndu helps people use AI and digital tools in practical ways. She is a former lecturer, a machine learning engineer, and the current CTO of a health tech startup. Through Tech Savvy Starts Here, she teaches with a clear, beginner friendly approach focused on practical use, clarity, and confidence.",
    icon: "User",
    imageSrc: siteConfig.headshotImage,
    imageAlt: "Dr. Ijeoma Ndu headshot",
  },
  finalCta: {
    title: "Build Your Confidence With Technology",
    description:
      "Join the newsletter, explore practical resources, and start learning digital skills that move you forward.",
    primaryCta: {
      label: "Subscribe",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "Workshop Updates",
      href: "/workshops",
      external: false,
    },
    tone: "dark",
  },
});
